import React, {
  ChangeEventHandler,
  ClipboardEventHandler,
  FocusEventHandler,
  HTMLInputAutoCompleteAttribute,
  useState
} from "react";
import { InputBaseProps } from "..";
import { SanityInputTextProps } from "../../../models/form/SanityFormInputField";
import { isNullOrWhiteSpace } from "../../../shared/string";
import { autoCompleteSupportedFields } from "../helpers";
import { CustomValidatorFunction, useFieldValidator } from "../Validation";
import InputContainer from "./InputContainer";
import { useFieldCustom } from "./useFieldCustom";

type CalculateCssClassesProps<TValue> = {
  error?: boolean;
  focused?: boolean;
  forceRaiseLabel?: boolean;
  inlineLabel: boolean;
  readonly?: boolean;
  value: TValue;
};

const calculateCssClasses = <TValue,>({
  error,
  focused,
  forceRaiseLabel,
  inlineLabel,
  readonly,
  value
}: CalculateCssClassesProps<TValue>) => {
  let inputClasses =
    "w-full px-4 outline-none text-brand-primary-20 text-lg rounded border appearance-none";
  let labelClasses: string;

  if (inlineLabel) {
    labelClasses =
      "absolute left-0 ml-4 transform transition-all duration-100 pointer-events-none";

    if (
      (!readonly && focused) ||
      forceRaiseLabel ||
      (typeof value === "string" && !isNullOrWhiteSpace(value)) ||
      value
    ) {
      labelClasses += " top-0 text-sm mt-3 transform-none";
    } else {
      labelClasses += " mt-0 top-1/2 -translate-y-1/2 text-lg";
    }

    if (!readonly && focused) {
      labelClasses += " text-brand-primary-50";
    } else {
      labelClasses += " text-label";
    }
  } else {
    labelClasses = "block mb-4 text-brand-primary-20 text-lg lg:text-2xl";
  }

  if (error) {
    inputClasses += " border-brand-tertiary-50 shadow-outline-error";
  } else {
    inputClasses +=
      " border-input-border focus:border-input-border-focused focus:shadow-outline";
  }

  if (readonly) {
    inputClasses += " bg-input-bg-readonly";
  }

  return {
    inputClasses,
    labelClasses
  };
};

export type GenericTextInputProps = InputBaseProps<SanityInputTextProps> & {
  type: "text" | "email" | "number" | "search" | "password" | "date" | "datetime-local";
  autoComplete?: HTMLInputAutoCompleteAttribute;
  inputMode?: "numeric";
  customValidator?: CustomValidatorFunction;
  onChange?: ChangeEventHandler<HTMLInputElement>;
};

const GenericTextInput = (props: GenericTextInputProps) => {
  const {
    autoComplete,
    name,
    hidden = false,
    inputContainerVariant,
    inputMode,
    type,
    placeholder: placeholderProps,
    label,
    readonly,
    validation,
    customValidator,
    onChange: onChangeCallback
  } = props;

  const isDateOrTimeInput = type === "date" || type === "datetime-local";

  const validator = useFieldValidator(validation, customValidator);
  const [field, meta] = useFieldCustom({
    name,
    type,
    validate: validator
  });

  const [focused, setFocused] = useState(false);

  const onFocus: FocusEventHandler<HTMLInputElement> = () => {
    setFocused(true);
  };

  const onBlur: FocusEventHandler<HTMLInputElement> = e => {
    field.onBlur(e);
    setFocused(false);
  };

  const onChange: ChangeEventHandler<HTMLInputElement> = e => {
    onChangeCallback?.(e);
    field.onChange(e);
  };

  const onPaste: ClipboardEventHandler<HTMLInputElement> = e => {
    if (onChangeCallback) {
      setTimeout(
        e => onChangeCallback(e as unknown as React.ChangeEvent<HTMLInputElement>),
        0,
        e
      );
    }
  };

  let inputPadding: string;

  if (label?.length && inputContainerVariant === "default") {
    if (
      focused ||
      isDateOrTimeInput ||
      (typeof meta.value !== "string" &&
        meta.value !== undefined &&
        meta.value !== null) ||
      !isNullOrWhiteSpace(meta.value)
    ) {
      inputPadding = "pt-9 pb-4";
    } else {
      inputPadding = "py-7";
    }
  } else {
    inputPadding = "py-4";
  }

  const { labelClasses, inputClasses } = calculateCssClasses({
    error: meta.touched && !!meta.error,
    focused,
    forceRaiseLabel: isDateOrTimeInput,
    inlineLabel: inputContainerVariant === "default",
    readonly,
    value: meta.value
  });

  const placeholder =
    inputContainerVariant === "sectioned" || !label?.length || focused
      ? placeholderProps
      : undefined;

  return (
    <InputContainer
      additionalClassName={hidden ? "hidden" : ""}
      meta={meta}
      variant={inputContainerVariant}
    >
      {label && !hidden && (
        <label htmlFor={name} className={labelClasses}>
          {label}
        </label>
      )}

      <input
        {...field}
        autoComplete={
          autoComplete || (autoCompleteSupportedFields.includes(name) ? "on" : "off")
        }
        className={`${inputPadding} ${inputClasses}`}
        hidden={hidden}
        id={name}
        inputMode={inputMode}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        onPaste={onPaste}
        placeholder={placeholder}
        readOnly={readonly}
        type={type ?? "text"}
      />
    </InputContainer>
  );
};

export default GenericTextInput;

